/**
 * Returns false for null, undefined, placeholder (1025), or invalid Date.
 * Use to guard date rendering and avoid showing <time> for fake/invalid dates.
 */
export function isValidDate(
  value: string | number | Date | null | undefined
): boolean {
  if (value == null) return false;
  const d = new Date(value as string | number | Date);
  if (Number.isNaN(d.getTime())) return false;
  if (d.getFullYear() === 1025) return false;
  return true;
}

/**
 * Returns a valid Date or null for null, undefined, invalid string, or invalid Date.
 * Use when parsing API date fields (e.g. response.deathDate) to avoid Invalid Date → .getDate() etc. → NaN.
 */
export function parseDateOrNull(
  value: string | number | Date | null | undefined
): Date | null {
  if (!isValidDate(value)) return null;
  return new Date(value as string | number | Date);
}

export function formatToDottedDate(
  dateInput: string | number | Date
): string | null {
  let date: Date;

  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === "number") {
    date = new Date(dateInput);
  } else if (typeof dateInput === "string") {
    const parsed = Date.parse(dateInput);
    if (isNaN(parsed)) {
      console.warn("Invalid date string:", dateInput);
      return null;
    }
    date = new Date(parsed);
  } else {
    console.warn("Unsupported date input:", dateInput);
    return null;
  }

  const day = date.getDate();
  const month = date.getMonth() + 1; // Months start at 0
  const year = date.getFullYear();

  const dayStr = String(day).padStart(2, "0");
  const monthStr = String(month).padStart(2, "0");

  return `${dayStr}.${monthStr}.${year}`;
}

export function formatDayAndTimeSlovenian(
  timestamp: number | null | undefined
): { dayName: string; time: string } {
  if (timestamp == null) {
    return { dayName: "", time: "" };
  }
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    return { dayName: "", time: "" };
  }
  const dayName = date.toLocaleDateString("sl-SI", { weekday: "long" });
  const time = date.toLocaleTimeString("sl-SI", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { dayName, time };
}



export const formatDate = (timestamp: string, options: Intl.DateTimeFormatOptions = {
  weekday: "long",
  month: "2-digit",
  day: "2-digit",
}) => {
  return new Date(timestamp).toLocaleString("sl-SI", options);
};


export const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};